import { Button, Input } from '@arco-design/mobile-react';
import { IconCheck, IconSearch } from '../components/icons';
import MobileSelect from '../components/MobileSelect';
import MobileTag, { type MobileTagTone } from '../components/MobileTag';
import MobileUserChip from '../components/MobileUserChip';
import type { MobileTask, TaskGroup } from '../types';

const groups: Array<{ key: TaskGroup; label: string }> = [
  { key: 'todo', label: '待办' }, { key: 'done', label: '已办' }, { key: 'cc', label: '抄送' }, { key: 'initiated', label: '发起' }
];

type Props = {
  tasks: MobileTask[];
  group: TaskGroup;
  onGroupChange: (group: TaskGroup) => void;
  onOpenTask: (id: string) => void;
  onMarkAllCcRead: () => void;
  counts: Record<TaskGroup, number>;
  keyword: string;
  onKeywordChange: (value: string) => void;
  workflowName: string;
  workflowNames: string[];
  onWorkflowNameChange: (value: string) => void;
  total: number;
  loading: boolean;
  error?: string;
  hasMore: boolean;
  markingRead: boolean;
  onRetry: () => void;
  onLoadMore: () => void;
};

function formatCardDate(value: string) {
  const explicitDate = value.match(/\b\d{4}-\d{2}-\d{2}\b/);
  if (explicitDate) return explicitDate[0];

  const today = new Date();
  const date = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (value.startsWith('昨天')) date.setDate(date.getDate() - 1);
  else if (!value.startsWith('今天')) {
    const monthDay = value.match(/(\d{1,2})\s*月\s*(\d{1,2})\s*日/);
    if (!monthDay) return value;
    return `${today.getFullYear()}-${monthDay[1].padStart(2, '0')}-${monthDay[2].padStart(2, '0')}`;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function taskCardDate(task: MobileTask) {
  return formatCardDate(task.group === 'done' ? task.updatedAt : task.receivedAt);
}

function taskKeyFields(task: MobileTask) {
  return task.summaryItems.slice(0, 4);
}

function MobileTasksView(props: Props) {
  const { keyword, workflowName, workflowNames } = props;
  const visibleTasks = props.tasks;
  const ccUnread = props.counts.cc;

  const changeGroup = (group: TaskGroup) => {
    props.onGroupChange(group);
  };

  return <div className="mobile-page mobile-tasks">
    <div className="mobile-task-filter-panel">
      <div className="mobile-task-search">
        <Input className="mobile-control-input" border="none" prefix={<IconSearch />} clearable value={keyword} placeholder="搜索审批名称、内容或发起人" onInput={(_, value) => props.onKeywordChange(value)} />
      </div>
      <nav className="mobile-segmented mobile-task-groups" aria-label="任务分类">
        {groups.map((item) => <button key={item.key} type="button" className={props.group === item.key ? 'is-active' : ''} onClick={() => changeGroup(item.key)}>
          {item.label}<em>{props.counts[item.key]}</em>
        </button>)}
      </nav>
      <div className="mobile-task-flow-filter">
        <MobileSelect value={workflowName} onChange={props.onWorkflowNameChange} ariaLabel="按审批流程名称筛选" placement="top" showDrawerTitle={false}
          options={[{ label: '全部审批流程', value: 'all' }, ...workflowNames.map((name) => ({ label: name, value: name }))]} />
        {props.group === 'cc'
          ? <Button className="mobile-cc-read-button" size="small" icon={<IconCheck />} disabled={ccUnread === 0 || props.markingRead} onClick={props.onMarkAllCcRead}>全部已读</Button>
          : <span className="mobile-task-tools__count">{props.total} 项</span>}
      </div>
    </div>
    <div className="mobile-task-list" aria-busy={props.loading}>
      {props.error ? <div className="mobile-empty" role="alert"><strong>任务加载失败</strong><span>{props.error}</span><button type="button" onClick={props.onRetry}>重新加载</button></div> : null}
      {visibleTasks.map((task) => {
        const displayedStatus = task.group === 'cc' ? (task.unread ? '抄送未读' : '抄送已读') : task.status;
        const statusTone: MobileTagTone = task.group === 'cc' ? (task.unread ? 'blue' : 'gray') : task.group === 'done' ? 'green' : task.urgent ? 'red' : 'blue';
        return <button className="mobile-task-card" type="button" key={task.id} onClick={() => props.onOpenTask(task.id)}>
          <div className="mobile-task-card__head">
            <strong>{task.title}</strong>
            <MobileTag tone={statusTone}>{displayedStatus}</MobileTag>
          </div>
          <span className="mobile-task-card__fields">
            {taskKeyFields(task).map((field) => <span className="mobile-task-card__field" key={`${task.id}-${field.label}`}>
              <span>{field.label}：</span><span>{field.value}</span>
            </span>)}
          </span>
          <span className="mobile-task-card__footer">
            <MobileUserChip name={task.initiator} />
            <small>{taskCardDate(task)}</small>
          </span>
        </button>;
      })}
      {!props.loading && !props.error && visibleTasks.length === 0 ? <div className="mobile-empty"><strong>当前没有匹配的任务</strong><span>可以切换分类或清除筛选条件。</span></div> : null}
      {props.loading ? <div className="mobile-empty" role="status">正在加载任务</div> : props.hasMore ? <button className="mobile-task-load-more" type="button" onClick={props.onLoadMore}>加载更多任务</button> : visibleTasks.length ? <div className="mobile-empty">暂无更多数据</div> : null}
    </div>
  </div>;
}

export default MobileTasksView;
